import type {
  Resource,
  Service,
  ServiceRequirement,
  ServiceResourceLink,
  ServiceResourceLinkInput
} from '@/api/mcp-appointments';
import { resourceHasServiceRole } from '@/utils/serviceRoles';

export interface RoleCoverage {
  active: number;
  inactive: number;
  required: number;
  isSufficient: boolean;
}

/** Map persisted service links to PUT payload rows. */
export function linksFromService(service: Pick<Service, 'resources'>): ServiceResourceLinkInput[] {
  return (service.resources ?? []).map((link) => ({
    resourceId: link.resourceId,
    serviceRoleId: link.serviceRoleId,
    isActive: link.isActive
  }));
}

/** Deduplicate by resourceId (last wins). */
export function dedupeServiceResourceLinks(
  links: ServiceResourceLinkInput[]
): ServiceResourceLinkInput[] {
  const byId = new Map<string, ServiceResourceLinkInput>();
  for (const link of links) {
    if (!link.resourceId || !link.serviceRoleId) {
      continue;
    }
    byId.set(link.resourceId, {
      resourceId: link.resourceId,
      serviceRoleId: link.serviceRoleId,
      isActive: link.isActive ?? true
    });
  }
  return Array.from(byId.values());
}

/** Filter links for one service role. */
export function linksForServiceRole(
  links: ServiceResourceLinkInput[],
  serviceRoleId: string
): ServiceResourceLinkInput[] {
  if (!serviceRoleId) {
    return [];
  }
  return links.filter((link) => link.serviceRoleId === serviceRoleId);
}

/** Company resources that can be linked under this role. */
export function eligibleResourcesForServiceRole(
  resources: Resource[] | undefined,
  serviceRoleId: string,
  excludeResourceIds: string[] = []
): Resource[] {
  if (!serviceRoleId) {
    return [];
  }
  const excluded = new Set(excludeResourceIds);
  return (resources ?? []).filter(
    (resource) => !excluded.has(resource.id) && resourceHasServiceRole(resource, serviceRoleId)
  );
}

/**
 * Display name of a resource: its first translation's name, or `fallback` when the resource
 * is missing or unnamed.
 */
export function getResourceDisplayName(
  resource: { translations?: Array<{ name?: string | null }> } | null | undefined,
  fallback: string
): string {
  return resource?.translations?.[0]?.name?.trim() || fallback;
}

/** Active/inactive counts vs required quantity for one role. */
export function countRoleCoverage(
  links: ServiceResourceLinkInput[],
  requirement: Pick<ServiceRequirement, 'serviceRoleId' | 'quantity'>
): RoleCoverage {
  const roleLinks = linksForServiceRole(links, requirement.serviceRoleId);
  let active = 0;
  let inactive = 0;
  for (const link of roleLinks) {
    if (link.isActive !== false) {
      active += 1;
    } else {
      inactive += 1;
    }
  }
  const required = requirement.quantity;
  return {
    active,
    inactive,
    required,
    isSufficient: active >= required
  };
}

/** Coverage for every requirement on the service. */
export function getRequirementsCoverageIssues(
  requirements: Array<Pick<ServiceRequirement, 'serviceRoleId' | 'quantity'>>,
  links: ServiceResourceLinkInput[]
): Array<{ serviceRoleId: string; active: number; required: number }> {
  return requirements
    .map((req) => {
      const coverage = countRoleCoverage(links, req);
      return {
        serviceRoleId: req.serviceRoleId,
        active: coverage.active,
        required: coverage.required
      };
    })
    .filter((item) => item.active < item.required);
}

/** Build link lookup with embedded resource for display (from last GET). */
export function buildLinkResourceMap(
  serviceLinks: ServiceResourceLink[] | undefined
): Map<string, Resource> {
  const map = new Map<string, Resource>();
  for (const link of serviceLinks ?? []) {
    if (link.resource) {
      map.set(link.resourceId, link.resource);
    }
  }
  return map;
}

/** Drop links whose service role is not in the current requirements list. */
export function pruneLinksToRequirementIds(
  links: ServiceResourceLinkInput[],
  requirementRoleIds: string[]
): ServiceResourceLinkInput[] {
  const allowed = new Set(requirementRoleIds.filter(Boolean));
  return links.filter((link) => link.serviceRoleId && allowed.has(link.serviceRoleId));
}
